import cx from 'clsx';
import { secondsToTimeSpan } from 'common/date.ts';
import dayjs from 'common/dayjs.ts';
import { GET, getRuntimeTimezone, isGranted, truncate } from 'common/helpers.ts';
import { lowerCase, upperFirst } from 'es-toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import useSWR from 'swr';
import type { LegRowUpdate, Leg as TLeg } from 'types/Leg.ts';
import type { ROLE } from 'types/Role.ts';
import { Button } from 'ui/component/Button.tsx';
import { ICamera, ICheckDouble, IEdit, IInfo, IIntegration, IMessage, IWarningAmber } from 'ui/component/Icons.tsx';
import { Stepper } from 'ui/component/Stepper.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import { DocumentForbidden } from '#admin/component/DocumentForbidden.tsx';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';
import { useResourcePermissions } from '#admin/hook/useResourcePermissions.tsx';
import { Datetime } from './Datetime.tsx';
import { DocumentActions } from './DocumentActions.tsx';

type Props = {
  leg: TLeg;
  index: number;
  onSupplierAssign: (leg: TLeg) => () => void;
  onDriverAssign: (leg: TLeg) => () => void;
  onOpenUploadDialog: (legs: TLeg[]) => () => void;
  onOpenLegEditDialog: (leg: TLeg) => () => void;
  onDeleteDocument: () => void;
  role: ROLE;
  // Optional permissions - if not provided, will use individual permission checking
  permissions?: {
    canAssignSupplier?: boolean;
    canAssignDriver?: boolean;
    canEditLeg?: boolean;
  };
  bridgeMode?: boolean;
};

const trackingmoreInfoToString = (data: LegRowUpdate['trackingmore_data'] | null): string => {
  if (!data) return '';

  let result = 'Tracking Updates\n------------------------\n';
  // each item in the data array is an object with key/value pairs, with hawb being the HAWB reference number (we're putting this as first)
  for (const entry of data) {
    result += `\nHAWB: ${entry.hawb}\n`;
    for (const [key, value] of Object.entries(entry).filter(([k]) => k !== 'hawb')) {
      result += `${upperFirst(lowerCase(key))}: ${value}\n`;
    }
  }
  result = result.trim();
  return result;
};

export const Leg = ({ leg, index, onSupplierAssign, onDriverAssign, onOpenUploadDialog, onOpenLegEditDialog, onDeleteDocument, permissions, bridgeMode = false }: Props) => {
  const { t } = useTranslation();
  const { me } = useMe();
  const env = useEnv();

  const { data: notifStatus } = useSWR(bridgeMode ? null : [`/leg/${leg.id}/driver-notifications-status`], GET);

  // use provided permissions or fall back to individual permission checking, only fetch individual permissions if no batch permissions provided
  const { getPermission } = useResourcePermissions(permissions ? null : leg.id, ['LegAssignSupplier', 'LegAssignDriver']);

  const canAssignSupplier = !bridgeMode && (permissions?.canAssignSupplier ?? getPermission('LegAssignSupplier') ?? (leg.owner_id === me?.owner_id || isGranted(me, '*', '*')));
  const canAssignDriver = !bridgeMode && (permissions?.canAssignDriver ?? getPermission('LegAssignDriver') ?? (leg.supplier_id === me?.owner_id || isGranted(me, '*', '*')));
  const canEditLeg = !bridgeMode && (permissions?.canEditLeg ?? (leg.supplier_id === me?.owner_id || isGranted(me, '*', '*')));

  return (
    <div key={index} data-id={leg.id} className="paper !bg-blue-sky mt-6 flex snap-y snap-start flex-col gap-3 last:mb-6" data-leg={index + 1} data-leg-status={leg.status}>
      <div className="flex flex-row justify-between">
        <Stepper
          className="w-[100px]"
          steps={[String(index + 1), String(index + 2)]}
          currentStep={leg.status === 'delivered' ? String(index + 2) : leg.status === 'transiting' ? String(index + 1) : 'xyz'}
          currentLegStatus={leg.status}
        />
        {canEditLeg && (
          <span onClick={onOpenLegEditDialog(leg)} className="p-1 text-blue hover:cursor-pointer hover:bg-gray-100">
            <Tooltip text={t('components.leg.tooltips.editLeg')}>
              <IEdit size={17} />
            </Tooltip>
          </span>
        )}
      </div>
      <LegDetailItem label={t('components.leg.labels.logistics_ex_id')}>{leg.logistics_ex_id || '-'}</LegDetailItem>
      <hr />
      <LegDetailItem label={t('components.leg.labels.supplier')}>
        <span className="max-w-full justify-self-start truncate py-3">
          <span className="max-w-full truncate py-3">
            {!leg.supplier && (leg.destination_actual_datetime || !canAssignSupplier) && <span>-</span>}
            {!leg.supplier && !leg.destination_actual_datetime && canAssignSupplier && (
              <Button onClick={onSupplierAssign(leg)} className="blue-outlined !min-w-[100px] !max-w-full text-nowrap normal-case" small={true}>
                {t('components.leg.buttons.assignSupplier')}
              </Button>
            )}
            {leg.supplier && (
              <div className="blue flex items-center justify-center gap-1">
                <Tooltip text={leg.supplier.name}>
                  {bridgeMode ? (
                    <span className="uppercase">{`${leg.supplier.name} - ${truncate(leg.supplier.id, 8, '').toUpperCase()}`}</span>
                  ) : (
                    <NavLink to={`/user/${leg.supplier.id}`} className="p-1 hover:bg-gray-100">
                      <span className="uppercase">{`${leg.supplier.name} - ${truncate(leg.supplier.id, 8, '').toUpperCase()}`}</span>
                    </NavLink>
                  )}
                </Tooltip>
                {!leg.destination_actual_datetime && canAssignSupplier && (
                  <span onClick={onSupplierAssign(leg)} className="p-1 hover:cursor-pointer hover:bg-gray-100">
                    <Tooltip text={t('components.leg.tooltips.reassignSupplier')}>
                      <IEdit size={17} />
                    </Tooltip>
                  </span>
                )}
              </div>
            )}
          </span>
        </span>
      </LegDetailItem>
      <LegDetailItem label={t('components.leg.labels.driver')}>
        <span className="max-w-full justify-self-start truncate py-3">
          {!leg.driver && (leg.destination_actual_datetime || !canAssignDriver) && <span>-</span>}
          <span className="max-w-full truncate py-3">
            {!leg.driver && !leg.destination_actual_datetime && canAssignDriver && (
              <Button onClick={onDriverAssign(leg)} className="blue-outlined !min-w-[100px] !max-w-full text-nowrap normal-case" small={true} disabled={!leg.supplier_id}>
                {t('components.leg.buttons.assignDriver')}
              </Button>
            )}
            {leg.driver && (
              <div className="blue flex items-center justify-center gap-1">
                <Tooltip text={leg.driver.name}>
                  {bridgeMode ? (
                    <span className="uppercase">{`${leg.driver.name} - ${truncate(leg.driver.id, 8, '').toUpperCase()}`}</span>
                  ) : (
                    <NavLink to={`/user/${leg.driver.id}`} className="p-1 hover:bg-gray-100">
                      <span className="uppercase">{`${leg.driver.name} - ${truncate(leg.driver.id, 8, '').toUpperCase()}`}</span>
                    </NavLink>
                  )}
                </Tooltip>
                {notifStatus !== 'none' && <ICheckDouble size={21} className={cx(notifStatus === 'sent' && 'grey', notifStatus === 'read' && 'blue')} />}
                {!leg.destination_actual_datetime && canAssignDriver && (
                  <span onClick={onDriverAssign(leg)} className="p-1 hover:cursor-pointer hover:bg-gray-100">
                    <Tooltip text={t('components.leg.tooltips.reassignDriver')}>
                      <IEdit size={17} />
                    </Tooltip>
                  </span>
                )}
              </div>
            )}
          </span>
        </span>
      </LegDetailItem>
      <hr />
      <LegDetailItem label={t('components.leg.labels.pickupAddress')}>
        {leg.origin_address_name && (
          <>
            <strong>{leg.origin_address_name}</strong>
            <span className="ml-2 block font-normal!">({leg.origin_address})</span>
          </>
        )}
        {!leg.origin_address_name && leg.origin_address}
      </LegDetailItem>
      {leg.origin_contact_name && (
        <LegDetailItem label={t('components.leg.labels.originContact')}>
          {leg.origin_contact_name}
          {leg.origin_contact_number && ` - ${leg.origin_contact_number}`}
        </LegDetailItem>
      )}
      <LegDetailItem label={t('views.shipment.labels.expectedPickupTime')}>
        <Datetime truncateRegion={true} rangeFrom={leg.origin_expected_from_datetime} timezone={leg.origin_tz || getRuntimeTimezone()}>
          {leg.origin_expected_datetime}
        </Datetime>
      </LegDetailItem>
      <LegDetailItem label={t('components.leg.labels.actualPickupTime')}>
        <div className="flex items-center justify-center gap-2">
          <Datetime timezone={leg.origin_tz || getRuntimeTimezone()} truncateRegion={true}>
            {leg.origin_actual_datetime}
          </Datetime>
          {leg.origin_comments && (
            <Tooltip text={leg.origin_comments}>
              <IMessage size={18} />
            </Tooltip>
          )}
          {leg.origin_manual_actual_datetime && (
            <Tooltip text={t('components.leg.tooltips.collectedManually')}>
              <IWarningAmber size={18} />
            </Tooltip>
          )}
          {leg.trackingmore && leg.supplier?.trackingmore_id && (
            <Tooltip text={trackingmoreInfoToString(leg.trackingmore_data)}>
              <IIntegration size={18} />
            </Tooltip>
          )}
        </div>
      </LegDetailItem>
      {leg.origin_waiting_started_at && leg.origin_actual_datetime && (
        <LegDetailItem label={t('components.leg.labels.originWaitingTime')}>
          {secondsToTimeSpan(dayjs(leg.origin_actual_datetime).diff(dayjs(leg.origin_waiting_started_at), 'second'), {
            daySuffix: t('labels.days.suffix'),
            hourSuffix: t('labels.hours.suffix'),
            minuteSuffix: t('labels.minutes.suffix'),
            secondSuffix: t('labels.seconds.suffix'),
            pad: true,
          })}
        </LegDetailItem>
      )}
      <hr />
      <LegDetailItem label={t('components.leg.labels.deliveryAddress')}>
        {leg.destination_address_name && (
          <>
            <strong>{leg.destination_address_name}</strong>
            <span className="ml-2 block font-normal!">({leg.destination_address})</span>
          </>
        )}
        {!leg.destination_address_name && leg.destination_address}
      </LegDetailItem>
      {leg.destination_contact_name && (
        <LegDetailItem label={t('components.leg.labels.destinationContact')}>
          {leg.destination_contact_name}
          {leg.destination_contact_number && ` - ${leg.destination_contact_number}`}
        </LegDetailItem>
      )}
      <LegDetailItem label={t('components.leg.labels.expectedDeliveryTime')}>
        <Datetime truncateRegion={true} rangeFrom={leg.destination_expected_from_datetime} timezone={leg.destination_tz || getRuntimeTimezone()}>
          {leg.destination_expected_datetime}
        </Datetime>
      </LegDetailItem>
      <LegDetailItem label={t('components.leg.labels.actualDeliveryTime')}>
        <div className="flex items-center justify-center gap-2">
          <Datetime timezone={leg.destination_tz || getRuntimeTimezone()} truncateRegion={true}>
            {leg.destination_actual_datetime}
          </Datetime>
          {leg.destination_comments && (
            <Tooltip text={leg.destination_comments}>
              <IMessage size={18} />
            </Tooltip>
          )}
          {leg.destination_manual_actual_datetime && (
            <Tooltip text={t('components.leg.tooltips.deliveredManually')}>
              <IWarningAmber size={18} />
            </Tooltip>
          )}
          {leg.trackingmore && leg.supplier?.trackingmore_id && (
            <Tooltip text={trackingmoreInfoToString(leg.trackingmore_data)}>
              <IIntegration size={18} />
            </Tooltip>
          )}
        </div>
      </LegDetailItem>
      {leg.destination_waiting_started_at && leg.destination_actual_datetime && (
        <LegDetailItem label={t('components.leg.labels.destinationWaitingTime')}>
          {secondsToTimeSpan(dayjs(leg.destination_actual_datetime).diff(dayjs(leg.destination_waiting_started_at), 'second'), {
            daySuffix: t('labels.days.suffix'),
            hourSuffix: t('labels.hours.suffix'),
            minuteSuffix: t('labels.minutes.suffix'),
            secondSuffix: t('labels.seconds.suffix'),
            pad: true,
          })}
        </LegDetailItem>
      )}
      <hr />

      {leg.supplier_reference_id && leg.supplier_reference_type === 'leg' && (
        <React.Fragment>
          <LegDetailItem label={'RTE'}>{leg.supplier_reference_id}</LegDetailItem>
          <hr />
        </React.Fragment>
      )}

      {leg.instructions && (
        <React.Fragment>
          <h3 className="body2">{t('components.leg.labels.instructions')}</h3>
          <span className="wrap-anywhere whitespace-pre-line">{leg.instructions}</span>
          <hr />
        </React.Fragment>
      )}

      {!bridgeMode && <h3 className="body2">{t('components.leg.labels.documents')}</h3>}
      {!bridgeMode && leg.documents.length > 0 && (
        <div className="flex flex-row flex-wrap gap-3">
          {leg.documents
            .filter((d) => d.type !== 'signature')
            .map((document) => (
              <DocumentActions
                documentId={document.id}
                key={document.id}
                documentUrl={document.url}
                disableDelete={!!document.logistics_ex_id}
                onDeleteSuccess={onDeleteDocument}
              />
            ))}
          {/*show all documents hidden to the user as well*/}
          {leg.documents_ids
            .filter((document_id) => !leg.documents.find((document) => document.id === document_id))
            .map((document_id) => (
              <DocumentForbidden key={document_id} id={document_id} />
            ))}
        </div>
      )}

      {!bridgeMode && env && isGranted(me, 'DocumentCreate', `${env.SUPERADMIN_GROUP_ID}/*`) && canEditLeg && (
        <Button LeftIcon={ICamera} className="blue-outlined relative mt-2 w-max gap-2" onClick={onOpenUploadDialog([leg])}>
          <div className="flex flex-row items-center gap-2">
            <span>{t('components.leg.buttons.uploadDoc')}</span>
            <Tooltip text={t('components.leg.tooltips.uploadDocument')}>
              <IInfo className="grey" size={16} />
            </Tooltip>
          </div>
        </Button>
      )}
    </div>
  );
};

type LegDetailItemProps = {
  className?: string;
  label: string;
  hasError?: boolean;
  children: React.ReactNode;
};

export const LegDetailItem = ({ className, label, hasError, children }: LegDetailItemProps) => (
  <span className={cx(className, 'flex flex-row place-content-between items-start gap-2')}>
    <span className="text-grey-label">{label}</span>
    <strong className={cx('text-right', hasError ? 'red' : null)}>{children}</strong>
  </span>
);
